import React from "react";
import {
  ArrowRight,
  Clock,
  Copy,
  Image as ImageIcon,
  RefreshCw,
  Square,
  Table2,
  Volume2,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";
import Icon from "../../../YourAIAgents/components/components/icon";
import ContentRenderer from "../ContentRenderer";
import ImageWithFallback from "../../../../components/ImageFallback";
import formatTime from "../../../../utils/formatTime";

/**
 * Action strip under every assistant reply. Mirrors the Lovable
 * MessageToolStrip row (Save and Automate → Copy → Table → Image →
 * Regenerate → Continue → Read aloud): same icons, same order, same
 * lucide sizing, with the agency Customization labels kept as tooltip
 * overrides where they already existed.
 */
const AIMessage = ({
  message,
  msgIndex,
  htmlContent,
  conversation_id,
  isLatestMessage,
  IsStreamApi,
  agencyInfo,
  copied,
  onCopy,
  onSaveAutomate,
  onCreateTable,
  onFormatImage,
  onTryAgain,
  onContinue,
  onSpeak,
  onStopSpeak,
  isSpeaking,
}) => {
  const customization = agencyInfo?.Customization;

  const actions = [
    {
      key: "automate",
      Icon: Clock,
      label: "Save and Automate",
      onClick: () => onSaveAutomate?.(message.content),
    },
    {
      key: "copy",
      Icon: Copy,
      label:
        customization?.copyicon_text && copied === msgIndex ? "Copied" : "Copy",
      onClick: () => onCopy?.(message.content, msgIndex),
    },
    {
      key: "table",
      Icon: Table2,
      label: customization?.table_text || "Format as table",
      onClick: () => onCreateTable?.(),
    },
    {
      key: "image",
      Icon: ImageIcon,
      label: "Format as image",
      onClick: () => onFormatImage?.(),
    },
    {
      key: "regenerate",
      Icon: RefreshCw,
      label: customization?.tryagain_text || "Regenerate",
      onClick: () => onTryAgain?.(),
    },
    {
      key: "continue",
      Icon: ArrowRight,
      label: customization?.continue_text || "Continue",
      onClick: () => onContinue?.(),
    },
    {
      key: "speak",
      Icon: isSpeaking ? Square : Volume2,
      label: isSpeaking ? "Stop" : "Read aloud",
      onClick: () =>
        isSpeaking ? onStopSpeak?.() : onSpeak?.(message.content, msgIndex),
    },
  ];

  return (
    <div className="flex flex-col items-start mb-5 group w-full">
      <div className="flex w-full items-start justify-between gap-3">
        <div className="text-foreground px-5 py-4 rounded-[10px] min-w-0 flex-1">
          <ContentRenderer
            index={msgIndex}
            htmlContent={htmlContent}
            conversation_id={conversation_id}
            isLatestMessage={isLatestMessage}
            IsStreamApi={IsStreamApi}
          />
        </div>
        <span className="shrink-0 pt-4 text-muted-foreground text-[11px] font-semibold">
          {formatTime(message.time)}
        </span>
      </div>

      <div className="flex items-center justify-between gap-3 w-full mt-2">
        <TooltipProvider delayDuration={150}>
          <div className="flex items-center gap-1 mt-1.5 pl-2 text-muted-foreground">
            {actions.map(({ key, Icon: ActionIcon, label, onClick }) => (
              <Tooltip key={key}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={onClick}
                    aria-label={label}
                    className="p-1 hover:text-foreground transition-colors"
                  >
                    <ActionIcon className="w-3.5 h-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">{label}</TooltipContent>
              </Tooltip>
            ))}
          </div>
        </TooltipProvider>

        {message.assistant_name && (
          <div className="flex items-center gap-1 text-[12px] text-muted-foreground">
            <Icon
              id="magicstar"
              style={{ marginRight: "4px", fontSize: "12px" }}
            />
            <span>
              {message.assistant_name?.includes("Agent builder for profile")
                ? "Agent Builder"
                : message.assistant_name}
            </span>
            {Array.isArray(message?.plugin_used) &&
              message.plugin_used.slice(0, 3).map((plugin, index) => (
                <ImageWithFallback
                  key={index}
                  src={`https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/${plugin?.brand
                    ?.toLowerCase()
                    ?.replaceAll(" ", "")}.svg`}
                  alt={plugin?.brand}
                />
              ))}
            {Array.isArray(message?.plugin_used) &&
              message.plugin_used.length > 3 && (
                <span>+ {message.plugin_used.length - 3} more</span>
              )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AIMessage;
